import React, { useRef } from "react";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { IconButton } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import QRWithLogo from "../RegistroFrom/QRWithLogo";
import { useCompetencia } from "../../../context/CompetenciaContext";

// --- utilidades de imagen/formatos ---
const loadImageAsDataUrl = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.src = url;
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = reject;
  });

const renderMarcaSiembra = (prueba) => {
  if (!prueba) return "Sin marca";
  const hasValue = Object.values(prueba).some(
    (v) => v !== undefined && v !== null && v !== "" && v !== 0
  );
  if (!hasValue) return "Sin marca";
  const { tipoMarca } = prueba;
  try {
    switch (tipoMarca) {
      case "velocidad":
        return `${prueba.marcaSegundos || 0}.${String(prueba.marcaCentesimas || "00").padEnd(2, "0")}`;
      case "medioFondo":
        return `${prueba.marcaMinutos || 0}:${String(prueba.marcaSegundos || "00").padStart(2, "0")}.${String(prueba.marcaCentesimas || "00").padEnd(2, "0")}`;
      case "fondo":
        return `${prueba.marcaHoras || 0}:${String(prueba.marcaMinutos || "00").padStart(2, "0")}:${String(prueba.marcaSegundos || "00").padStart(2, "0")}.${String(prueba.marcaCentesimas || "00").padEnd(2, "0")}`;
      case "campo":
        return `${prueba.marcaMetros || 0}.${String(prueba.marcaCentimetros || "00").padEnd(2, "0")}`;
      case "combinados":
        return `${prueba.marcaPuntos || 0} puntos`;
      default:
        return "Sin marca";
    }
  } catch {
    return "Sin marca";
  }
};

const formatMarcaRelevo = (relevo) => {
  const segundos = String(relevo.marcaSegundos || 0);
  const centesimas = String(relevo.marcaCentesimas || "00").padEnd(2, "0");
  if (relevo.marcaMinutos !== undefined && relevo.marcaMinutos !== null) {
    const minutos = relevo.marcaMinutos;
    return `${minutos}:${String(relevo.marcaSegundos || 0).padStart(2, "0")}.${centesimas}`;
  }
  return `${segundos}.${centesimas}`;
};

const renderDatosRelevo = (relevo) => {
  const lines = [];
  if (relevo.posicion) lines.push(`${relevo.posicion}`);
  return lines;
};

// --- layout PDF ---
const headerHeight = 20;
const footerHeight = 20;
const sponsorSpaceHeight = 20;
const bottomMargin = footerHeight + sponsorSpaceHeight;

const checkPageBreak = (doc, currentY, requiredSpace = 0) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (currentY + requiredSpace > pageHeight - bottomMargin) {
    doc.addPage();
    drawHeader(doc);
    doc.setTextColor(0, 0, 0);
    return headerHeight + 10;
  }
  return currentY;
};

const addDivider = (doc, currentY, margin = 14) => {
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.5);
  doc.line(margin, currentY, doc.internal.pageSize.getWidth() - margin, currentY);
  return currentY + 5;
};

let headerLogoDataUrl = null;
const drawHeader = (doc) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFillColor(doc.__colorPrimario || "#0066CC");
  doc.rect(0, 0, pageWidth, headerHeight, "F");
  doc.setTextColor(doc.__colorSecundario || "#FFFFFF");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text(doc.__competenciaNombre || "Competencia No Disponible", 14, headerHeight / 2 + 6);
  if (headerLogoDataUrl) {
    const headerImg = new Image();
    headerImg.src = headerLogoDataUrl;
    const maxLogoHeight = headerHeight - 4;
    const maxLogoWidth = 40;
    let desiredLogoHeight = maxLogoHeight;
    let desiredLogoWidth = (headerImg.width * desiredLogoHeight) / headerImg.height;
    if (desiredLogoWidth > maxLogoWidth) {
      desiredLogoWidth = maxLogoWidth;
      desiredLogoHeight = (headerImg.height * desiredLogoWidth) / headerImg.width;
    }
    doc.addImage(
      headerLogoDataUrl,
      "PNG",
      pageWidth - desiredLogoWidth - 14,
      (headerHeight - desiredLogoHeight) / 2,
      desiredLogoWidth,
      desiredLogoHeight
    );
  }
};

const PDFGenerator = ({ datos, competenciaId }) => {
  const qrRef = useRef(null);
  const { competencias } = useCompetencia();

  const competencia = competencias.find((c) => c.id === competenciaId) || {};
  const {
    logo,
    nombre: competenciaNombre = "Competencia No Disponible",
    colorPrimario = "#0066CC",
    colorSecundario = "#FFFFFF",
    precioInscripciones,
  } = competencia;

  const setDocGlobals = (doc) => {
    doc.__colorPrimario = colorPrimario;
    doc.__colorSecundario = colorSecundario;
    doc.__competenciaNombre = competenciaNombre;
  };

  // ✅ Fallback de pruebas: usa pruebasData o pruebas (del payload final)
  const pruebasList =
    Array.isArray(datos?.pruebasData) && datos.pruebasData.length
      ? datos.pruebasData
      : Array.isArray(datos?.pruebas)
      ? datos.pruebas
      : [];

  const generatePDF = async () => {
    try {
      const doc = new jsPDF();
      setDocGlobals(doc);
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const xLabel = 14;
      const lineHeight = 7;

      if (logo) {
        try {
          headerLogoDataUrl = await loadImageAsDataUrl(logo);
        } catch (e) {
          console.warn("Error cargando logo para el encabezado:", e);
        }
      }

      drawHeader(doc);
      doc.setTextColor(0, 0, 0);
      let y = headerHeight + 10;

      // --- Detalles + QR ---
      const detailsLines = [];
      if (datos?.codigoId) detailsLines.push(`Registro: ${datos.codigoId}`);
      if (datos?.nombre || datos?.apellido)
        detailsLines.push(`Nombre: ${`${datos?.nombre || ""} ${datos?.apellido || ""}`.trim()}`);
      if (datos?.rama) detailsLines.push(`Rama: ${datos.rama}`);
      if (datos?.categoria) {
        const catStr =
          typeof datos.categoria === "object" && datos.categoria !== null
            ? (datos.categoria.nombre || datos.categoria.id || "")
            : datos.categoria;
        detailsLines.push(`Categoría: ${catStr}`);
      }
      if (datos?.equipo) {
        detailsLines.push(
          "Equipo: " +
            (typeof datos.equipo === "object"
              ? `${datos.equipo.name} (${datos.equipo.teamCode})`
              : datos.equipo)
        );
      }
      if (datos?.diaNacimiento && datos?.mesNacimiento && datos?.anioNacimiento)
        detailsLines.push(
          `Fecha de nacimiento: ${datos.diaNacimiento}/${datos.mesNacimiento}/${datos.anioNacimiento}`
        );
      if (datos?.email) detailsLines.push(`Email: ${datos.email}`);
      if (datos?.telefono) detailsLines.push(`Teléfono: ${datos.telefono}`);
      if (precioInscripciones !== undefined)
        detailsLines.push(`Precio: $${Number(precioInscripciones).toFixed(2)}`);

      const maxWidthForValue = pageWidth - 60 - 20;
      let wrappedDetails = [];
      detailsLines.forEach((line) => {
        wrappedDetails = wrappedDetails.concat(doc.splitTextToSize(line, maxWidthForValue));
      });

      const pdfQRSize = 50;
      const extraQRText = 20;
      const effectiveQRHeight = pdfQRSize + extraQRText;
      const detailsHeight = lineHeight + wrappedDetails.length * lineHeight;
      const sectionHeight = Math.max(detailsHeight, effectiveQRHeight);

      y = checkPageBreak(doc, y, sectionHeight);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text("Detalles de la inscripción", xLabel, y);
      let currentY = y + lineHeight;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      wrappedDetails.forEach((line) => {
        doc.text(line, xLabel, currentY);
        currentY += lineHeight;
      });

      const qrCanvas = qrRef.current?.querySelector("canvas");
      if (qrCanvas && typeof qrCanvas.toDataURL === "function") {
        const qrX = pageWidth - pdfQRSize - 20;
        const qrY = y + (sectionHeight - effectiveQRHeight) / 2;
        doc.setDrawColor(colorPrimario);
        doc.setLineDash([1, 1]);
        doc.roundedRect(qrX - 5, qrY - 7, pdfQRSize + 10, effectiveQRHeight + 5, 5, 5, "S");
        doc.addImage(qrCanvas.toDataURL("image/png"), "PNG", qrX, qrY, pdfQRSize, pdfQRSize);
        doc.setTextColor(colorPrimario);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text("Escanear para verificar", qrX + pdfQRSize / 2, qrY + pdfQRSize + 4, { align: "center" });
        if (datos?.codigoId)
          doc.text(`Código: ${String(datos.codigoId)}`, qrX + pdfQRSize / 2, qrY + pdfQRSize + 10, { align: "center" });
        doc.setTextColor(0, 0, 0);
      }

      y += sectionHeight + 5;
      y = addDivider(doc, y);

      const printSection = (title, linesArray) => {
        let wrappedLines = [];
        linesArray.forEach((line) => {
          wrappedLines = wrappedLines.concat(doc.splitTextToSize(line, pageWidth - xLabel - 20));
        });
        const required = lineHeight + wrappedLines.length * lineHeight + 5;
        y = checkPageBreak(doc, y, required);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.text(title, xLabel, y);
        y += lineHeight;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(12);
        wrappedLines.forEach((line) => {
          doc.text(line, xLabel, y);
          y += lineHeight;
        });
        y = addDivider(doc, y);
      };

      // ✅ Sección 2: Pruebas Individuales (usa pruebasList)
      const pruebasIndividualesLines = [];
      if (Array.isArray(pruebasList)) {
        pruebasList.forEach((prueba) => {
          if (!prueba?.prueba) return;
          pruebasIndividualesLines.push(`Prueba: ${prueba.prueba}`);
          const marca = renderMarcaSiembra(prueba);
          if (marca) pruebasIndividualesLines.push(`Marca: ${marca}`);
          pruebasIndividualesLines.push("");
        });
      }
      if (pruebasIndividualesLines.length > 0) {
        printSection("Pruebas Individuales", pruebasIndividualesLines);
      }

      // Sección 3: Pruebas de Relevo
      const pruebasRelevoLines = [];
      if (Array.isArray(datos?.relevosData)) {
        datos.relevosData.forEach((relevo) => {
          if (!relevo?.prueba) return;
          let line = `Prueba: ${relevo.prueba}`;
          if (relevo.equipoTipo) line += ` (${relevo.equipoTipo})`;
          pruebasRelevoLines.push(line);
          pruebasRelevoLines.push(`Marca: ${formatMarcaRelevo(relevo)}`);
          renderDatosRelevo(relevo).forEach((l) => pruebasRelevoLines.push(l));
          pruebasRelevoLines.push("");
        });
      }
      if (pruebasRelevoLines.length > 0) {
        printSection("Pruebas de Relevo", pruebasRelevoLines);
      }

      // Asociación
      if (datos?.asociacion) printSection("Asociación", [`Asociación: ${datos.asociacion}`]);

      // Dirección
      const direccionLines = [];
      if (datos?.direccion) direccionLines.push(`Dirección: ${datos.direccion}`);
      if (datos?.ciudad) direccionLines.push(`Ciudad: ${datos.ciudad}`);
      if (datos?.estado) direccionLines.push(`Estado: ${datos.estado}`);
      if (datos?.pais) direccionLines.push(`País: ${datos.pais}`);
      if (direccionLines.length > 0) printSection("Dirección", direccionLines);

      // Tutor
      if (datos?.menorEdad && (datos?.tutorNombre || datos?.tutorEmail || datos?.tutorTelefono)) {
        const tutorLines = [];
        if (datos?.tutorNombre) tutorLines.push(`Nombre del tutor: ${datos.tutorNombre}`);
        if (datos?.tutorEmail) tutorLines.push(`Email del tutor: ${datos.tutorEmail}`);
        if (datos?.tutorTelefono) tutorLines.push(`Teléfono del tutor: ${datos.tutorTelefono}`);
        if (tutorLines.length > 0) printSection("Información del tutor", tutorLines);
      }

      // Contacto de emergencia
      if (Array.isArray(datos?.contactoEmergencia) && datos.contactoEmergencia.length > 0) {
        const contactoLines = [];
        datos.contactoEmergencia.forEach((c) => {
          if (c?.nombreEmergencia) contactoLines.push(`Nombre: ${c.nombreEmergencia}`);
          if (c?.telefonoEmergencia) contactoLines.push(`Teléfono: ${c.telefonoEmergencia}`);
          contactoLines.push("");
        });
        printSection("Contacto de emergencia", contactoLines);
      }

      // Footer
      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFillColor(colorPrimario);
        doc.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, "F");
        doc.setTextColor(colorSecundario);
        doc.setFontSize(10);
        doc.text(
          "Documento generado automáticamente - SportCompass",
          pageWidth / 2,
          pageHeight - footerHeight / 2 + 3,
          { align: "center" }
        );
      }

      const athleteName = `${(datos?.nombre || "").trim()}_${(datos?.apellido || "").trim()}`;
      const registrationNum = datos?.codigoId ? String(datos.codigoId) : "inscripcion";
      const fileName =
        athleteName !== "_" ? `${athleteName}_${registrationNum}.pdf` : `inscripcion_${registrationNum}.pdf`;
      doc.save(fileName);
    } catch (err) {
      console.error("Error generando el PDF:", err);
    }
  };

  return (
    <>
      <IconButton onClick={generatePDF} color="secondary">
        <DownloadIcon />
      </IconButton>
      {/* QR oculto para capturar imagen de alta resolución */}
      <div style={{ position: "absolute", top: "-1000px", left: "-1000px", opacity: 0 }} ref={qrRef}>
        <QRWithLogo
          value={`https://pistaycampofotos.photohawk.com/admin/es/photos/${datos?.codigoId || ""}`}
          size={200}
          logoSrc={logo}
          logoSize={40}
        />
      </div>
    </>
  );
};

export default PDFGenerator;
